import { Box, BoxProps, Flex, Icon, Skeleton } from '@chakra-ui/react'

import { AssociatedDevices } from '@/components/icons'

import { TailgateBox } from '../types/types'
import { getBoxInput, getBoxInputDefaultOrder } from '../utils/utils'

interface GridCoordsBoxIProps {
  box: TailgateBox
  title: string
  subTitle: string
  backgroundColour: string
  iconColour: string
  isValid: boolean
  loading?: boolean
}

export const GridCoordsBox = ({
  box,
  title,
  subTitle,
  backgroundColour,
  iconColour,
  isValid,
  loading,
}: GridCoordsBoxIProps) => {
  const isIncomplete = box?.corners?.length < 4

  // If the box is incomplete then populate topLeft, topRight, bottomRight, bottomLeft in order
  // regardless of their relative position.
  //
  // Once the box is complete use algorithm to detect correct labels.
  const { topLeft, topRight, bottomLeft, bottomRight } = isIncomplete
    ? getBoxInputDefaultOrder(box)
    : getBoxInput(box)

  return (
    <Flex
      backgroundColor={backgroundColour}
      borderRadius='0.375rem'
      data-testid='floorPlanPage_modal_gridCoorsBox'
      direction='column'
      m={5}
      minW='400px'
      mr={0}
      mt={0}
      p={5}
    >
      <Flex alignItems='center' direction='row' flex={1}>
        <Icon as={AssociatedDevices} boxSize='14px' strokeColor={iconColour} />
        <Box color={iconColour} fontSize='12px' fontWeight='bold' ml={2}>
          {title}
        </Box>
      </Flex>
      <Box color='#22272B' fontSize='12px' fontWeight='medium' mt={1}>
        {subTitle}
      </Box>

      {!isValid && (
        <Box color='#22272B' fontSize='14px' fontWeight='bold' mt={1}>
          Invalid box points
        </Box>
      )}

      <Flex alignItems='center' flex={1} mt={3}>
        {loading ? (
          <>
            <PointSkeleton label='Top left' />
            <PointSkeleton label='Top right' />
            <PointSkeleton label='Bottom right' />
            <PointSkeleton label='Bottom left' />
          </>
        ) : (
          <>
            <PointBox label='Top left' mr={8} point={topLeft} />
            <PointBox label='Top right' mr={8} point={topRight} />
            <PointBox label='Bottom right' mr={8} point={bottomRight} />
            <PointBox label='Bottom left' point={bottomLeft} />
          </>
        )}
      </Flex>
    </Flex>
  )
}

interface PointBoxIProps extends BoxProps {
  point: { x: number; y: number }
  label: string
}

export const PointBox = ({ point, label, ...rest }: PointBoxIProps) => {
  const x = point?.x ? Math.round(point?.x) : 0
  const y = point?.y ? Math.round(point?.y) : 0
  return (
    <Box {...rest}>
      <Box color='#22272B' fontSize='14px' fontWeight='medium' minH='25px'>
        {`(${x}, ${y})`}
      </Box>
      <Box color='#454F59' fontSize='12px' fontWeight='medium'>
        {label}
      </Box>
    </Box>
  )
}

export const PointSkeleton = ({ label }: { label: string }) => {
  return (
    <Box mr={8}>
      <Skeleton color='#22272B' fontWeight='medium' h='25px' mb={2} w='40px' />
      <Box color='#454F59' fontSize='12px' fontWeight='medium'>
        {label}
      </Box>
    </Box>
  )
}
